/**
 * Input Styles Utility
 * Centralized input styling to reduce duplication
 */

/**
 * Base input classes for standard form inputs
 */
export const baseInputClasses = 'w-full px-3 py-2 border border-gray-200 rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent'

/**
 * Small input variant (compact forms)
 */
export const smallInputClasses = 'w-full px-2 py-1.5 text-xs border border-gray-200 rounded-md focus:ring-2 focus:ring-primary focus:border-primary'

/**
 * Large input variant (emphasized forms)
 */
export const largeInputClasses = 'w-full px-4 py-3 border border-gray-200 rounded-lg text-lg focus:ring-2 focus:ring-primary focus:border-transparent'

/**
 * Input with error state
 * @param {boolean} hasError - Whether the input has a validation error
 * @returns {string} Complete className string
 */
export const getInputClasses = (hasError = false) => {
  const baseClasses = 'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent'
  const borderClass = hasError ? 'border-red-300' : 'border-gray-200'
  return `${baseClasses} ${borderClass}`
}

/**
 * Input with error state (small variant)
 * @param {boolean} hasError - Whether the input has a validation error
 * @returns {string} Complete className string
 */
export const getSmallInputClasses = (hasError = false) => {
  const baseClasses = 'w-full px-2 py-1.5 text-xs border rounded-md focus:ring-2 focus:ring-primary focus:border-primary'
  const borderClass = hasError ? 'border-red-300' : 'border-gray-200'
  return `${baseClasses} ${borderClass}`
}

/**
 * Auth form input classes (appearance-none for custom styling)
 * @param {boolean} hasError - Whether the input has a validation error
 * @returns {string} Complete className string
 */
export const getAuthInputClasses = (hasError = false) => {
  const baseClasses = 'mt-1 appearance-none relative block w-full px-3 py-2 border placeholder-gray-500 text-gray-900 rounded-md focus:outline-none focus:ring-primary focus:border-primary-500 text-sm'
  const borderClass = hasError ? 'border-red-300' : 'border-gray-200'
  return `${baseClasses} ${borderClass}`
}

/**
 * Textarea classes
 * @param {boolean} hasError - Whether the textarea has a validation error
 * @returns {string} Complete className string
 */
export const getTextareaClasses = (hasError = false) => {
  const baseClasses = 'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent resize-none'
  const borderClass = hasError ? 'border-red-300' : 'border-gray-200'
  return `${baseClasses} ${borderClass}`
}

/**
 * Select dropdown classes
 * @param {boolean} hasError - Whether the select has a validation error
 * @returns {string} Complete className string
 */
export const getSelectClasses = (hasError = false) => {
  const baseClasses = 'w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent text-sm'
  const borderClass = hasError ? 'border-red-300' : 'border-gray-200'
  return `${baseClasses} ${borderClass}`
}

/**
 * Disabled input additional classes
 */
export const disabledInputClasses = 'disabled:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-60'

/**
 * Read-only input classes
 */
export const readOnlyInputClasses = 'bg-gray-50 cursor-default'

/**
 * Label classes
 */
export const labelClasses = 'block text-sm font-medium text-gray-700 mb-1'
export const smallLabelClasses = 'block text-xs font-medium text-gray-700 mb-0.5'

/**
 * Error message classes
 */
export const errorMessageClasses = 'mt-1 text-xs text-red-600'

/**
 * Helper text classes
 */
export const helperTextClasses = 'mt-1 text-xs text-gray-500'
